/**
 * Interiors (ภายในอาคาร): the rooms behind the doors of a zone's buildings. Each interior is a set
 * of floors — the ground floor, rooms upstairs, a cellar or a cave below — and each floor is a small
 * map of its own, laid out here as a plan of characters (see `CELL`) and generated by
 * `generateInterior` (interiors.ts). Doors of the buildings outside open onto them by `DOOR_LINKS`.
 * Add a room here: its plan, look, people and rules; the world and the renderer follow.
 * Design: docs/INTERIORS.md.
 */
import type { Facing } from '../map';
import type { ZoneId } from './zones';
import { SECT_DOOR_LINKS, SECT_INTERIORS } from './layouts/sects';
import { CITY_DOOR_LINKS } from './layouts/cities';
import { DUNGEON_INTERIOR, DUNGEON_LINK } from './dungeon';

/**
 * The plan's characters. Walls and furniture are solid; everything else is floor. Stairs pair up
 * between floors: the k-th flight of `^` on a floor with the k-th flight of `v` on the floor above,
 * the k-th trapdoor `h` with the k-th ladder `u` on the floor below.
 */
export const CELL = {
  /** Outside the building (nothing is drawn there). */
  void: ' ',
  wall: '#',
  /** A wall with a window: daylight comes in. */
  window: 'w',
  /** The way out, in an outer wall of the ground floor. */
  door: 'D',
  floor: '.',
  /** A mat or rug on the floor. */
  mat: ',',
  /** A stool or a cushion (sat on, walked over). */
  stool: 'c',
  /** A lantern hanging over the floor: a light. */
  hanging: '*',
  /** Balcony boards, open to the sky. */
  balcony: 'b',
  /** The balcony's rail (low, solid). */
  rail: '=',
  up: '^',
  down: 'v',
  /** A ladder up through a hatch in the ceiling. */
  ladder: 'u',
  /** A trapdoor in the floor, a ladder down. */
  trapdoor: 'h',
  /** Where a resident (townsfolk at home) stands. */
  resident: 'r',
  /** Where a monster lurks (dangerous floors only). */
  monster: 'm',
  table: 'T',
  bed: 'B',
  counter: 'C',
  shelf: 'S',
  /** Stove or hearth: a light. */
  stove: 'K',
  /** Altar with candles: a light. */
  altar: 'A',
  /** Wine jars. */
  jar: 'W',
  /** Crates and barrels. */
  crate: 'X',
  pillar: 'P',
  /** A standing lantern: a light. */
  lantern: 'L',
  /** A folding screen. */
  screen: 'Z',
  /** A rack of fishing nets. */
  nets: 'N',
  chest: 'G',
  /** Cave rock (a wall of the cave). */
  rock: 'O',
  /** Water in a cave or cellar. */
  water: '~',
} as const;

/** Walls of the room (the renderer cuts the ones between the camera and the room down low). */
export const WALL_CELLS = '#wDO';
/** Furniture that blocks the way. */
export const SOLID_CELLS = 'TBCSKAWXPLZNG=';
/** Stairs, ladders and trapdoors: stepping on one takes the player to another floor. */
export const STAIR_CELLS = '^vuh';
/** Cells that give light. */
export const LIGHT_CELLS = '*LKA';
/** The flight on the floor above or below that each kind of stairs arrives at. */
export const STAIR_PAIR: Readonly<Record<string, string>> = { '^': 'v', v: '^', h: 'u', u: 'h' };
/** Floors up (+1) or down (−1) for each kind of stairs. */
export const STAIR_DIR: Readonly<Record<string, 1 | -1>> = { '^': 1, u: 1, v: -1, h: -1 };

/** What may be done inside. Each floor may override its building's rules. */
export interface IndoorRules {
  /** วิ่งพันลี้ (Shift). */
  readonly sprint: boolean;
  /** Jumping, dashing and the other qinggong arts. */
  readonly jump: boolean;
  /** Riding (and the flying sword). */
  readonly mount: boolean;
  /** Fighting: attacks, moves, auto-battle, PK. */
  readonly combat: boolean;
  /** No killing of players. */
  readonly safe: boolean;
}

export const DEFAULT_RULES: IndoorRules = {
  sprint: false,
  jump: false,
  mount: false,
  combat: false,
  safe: true,
};
/** A cave or dungeon floor: fighting allowed, still no running or leaping in the tight passages. */
export const DANGER_RULES: Partial<IndoorRules> = { combat: true, safe: false };

export interface InteriorNpc {
  /** The digit in the plan where they stand ('1'–'9'). */
  readonly spot: string;
  readonly name: string;
  readonly title: string;
  readonly sprite: string;
  /** Said in turn each time they are spoken to. `{name}` is the player's name. */
  readonly lines: readonly string[];
  /** Sells the merchant's goods. */
  readonly shop?: boolean;
}

/** Floor and wall materials. */
export type FloorLook = 'plank' | 'stone' | 'earth' | 'tile';
export type WallLook = 'plaster' | 'timber' | 'stone' | 'rock';

export interface FloorDef {
  /** 0 the ground floor, 1 upstairs, −1 a cellar, −2 deeper still. */
  readonly level: number;
  readonly name: string;
  readonly plan: readonly string[];
  readonly floor: FloorLook;
  readonly wall: WallLook;
  /** How dark it is away from the lights (0 daylight through the windows – 1 pitch black). */
  readonly dark: number;
  readonly npcs?: readonly InteriorNpc[];
  /** Monsters at the `m` spots, in order. */
  readonly monsters?: readonly { readonly monster: string; readonly n: number }[];
  readonly rules?: Partial<IndoorRules>;
  /**
   * The way down from here stays shut until the main quest has reached `quest` (an index into
   * QUESTS), with `text` said when it is tried before then.
   */
  readonly sealed?: { readonly quest: number; readonly text: string };
}

export type InteriorTemplate = 'house' | 'inn' | 'shop' | 'hall' | 'cellar' | 'cave';

export interface InteriorDef {
  readonly id: string;
  readonly name: string;
  readonly zone: ZoneId;
  /** The shared layout it is built from (for the docs and the renderer's details). */
  readonly template: InteriorTemplate;
  /**
   * The side of the building its front door faces (the exterior door's facing). Plans are drawn
   * with the door at the bottom (south); the floors are turned to match. South if left out.
   */
  readonly facing?: Facing;
  /** Variation of a shared plan: mirrored left to right, and cells swapped (e.g. nets for crates). */
  readonly mirror?: boolean;
  readonly swap?: Readonly<Record<string, string>>;
  /** Seed for the look of the furniture (wood tones, what lies on the tables). */
  readonly seed: number;
  readonly floors: readonly FloorDef[];
  readonly rules?: Partial<IndoorRules>;
  /** Townsfolk (folk ids) who live here: while they are home, they are inside at the `r` spots. */
  readonly residents?: readonly string[];
}

/**
 * A door of a building outside. `building` is the id the zone's map gives the building
 * (`Structure.id`, with its door in `Structure.door`); `door` picks which `D` of the ground floor
 * it opens onto, in reading order.
 */
export interface DoorLink {
  readonly zone: ZoneId;
  readonly building: string;
  readonly interior: string;
  readonly door?: number;
}

// ───────────────────────── shared plans ─────────────────────────

/** A one-room home: a bed, a hearth, a table, a rack by the wall (swapped per household). */
const HOUSE: readonly string[] = [
  '##w####w##',
  '#B.S..NN.#',
  '#B..r....#',
  '#..*..T..#',
  '#Z...cTc.#',
  '#........#',
  '#K.X..r..#',
  '#####D####',
];

const house = (
  id: string,
  zone: ZoneId,
  name: string,
  seed: number,
  o: { facing?: Facing; mirror?: boolean; swap?: Record<string, string>; residents?: string[] } = {},
): InteriorDef => ({
  id,
  name,
  zone,
  template: 'house',
  seed,
  ...o,
  floors: [{ level: 0, name, plan: HOUSE, floor: 'plank', wall: 'plaster', dark: 0.3 }],
});

const list: InteriorDef[] = [
  {
    id: 'yanyu-inn',
    facing: 'e',
    name: 'โรงเตี๊ยมลมทะเล',
    zone: 'yanyu',
    template: 'inn',
    seed: 11,
    residents: ['liu'],
    floors: [
      {
        level: 0,
        name: 'โรงเตี๊ยมลมทะเล · ห้องโถง',
        floor: 'plank',
        wall: 'timber',
        dark: 0.22,
        plan: [
          '##w###w####w##',
          '#SSK.CCC1...^#',
          '#...r.......^#',
          '#*..........L#',
          'w.cTc..cTc...#',
          '#..T....T..*.#',
          '#.c.c..c.c...#',
          'w....*.....v.#',
          '#.cTc..2.....#',
          '#W.T.......XX#',
          '######D#######',
        ],
        npcs: [
          {
            spot: '1',
            name: 'เสี่ยวเอ้อ',
            title: 'เด็กรับใช้โรงเตี๊ยม',
            sprite: 'fisherKid',
            lines: [
              'เชิญขอรับ! ชาสักกา หรือจะขึ้นไปพักห้องชั้นบนก็ได้',
              'ห้องเก็บสุราอยู่ข้างล่าง นายหญิงไม่ให้ใครลงไปจิบเล่นนะขอรับ',
              'ระเบียงชั้นบนมองเห็นท่าเรือทั้งหมด ยามค่ำงามนัก',
            ],
          },
          {
            spot: '2',
            name: 'พ่อค้าเกลือ',
            title: 'แขกโรงเตี๊ยม',
            sprite: 'merchant',
            lines: [
              'ข้าพักที่นี่รอเรือขึ้นเหนือ ลมทะเลปีนี้แรงผิดปกติ',
              'ได้ยินว่าคนแปลกหน้าคนหนึ่งตื่นขึ้นบนสะพานท่าเรือ… หรือว่าเป็นเจ้า {name}?',
            ],
          },
        ],
      },
      {
        level: 1,
        name: 'โรงเตี๊ยมลมทะเล · ห้องพักชั้นบน',
        floor: 'plank',
        wall: 'timber',
        dark: 0.25,
        plan: [
          '##############',
          '#B.S#B.S#BB.S#',
          '#B..#B..#..c.#',
          '#.*.#.*.#.*T.#',
          '##.###.###.###',
          '#v..*.1..*..L#',
          '#v...........#',
          '####w#..#w####',
          ' =bbbbbbbbbb= ',
          ' ============ ',
        ],
        npcs: [
          {
            spot: '1',
            name: 'บัณฑิตเดินทาง',
            title: 'แขกห้องชั้นบน',
            sprite: 'scholar',
            lines: [
              'ข้าเดินทางไปสอบที่เมืองหลวง พักเหนื่อยที่นี่สักสองสามคืน',
              'ออกไปที่ระเบียงสิ จะเห็นเรือประมงกลับฝั่งยามเย็น',
            ],
          },
        ],
      },
      {
        level: -1,
        name: 'โรงเตี๊ยมลมทะเล · ห้องเก็บสุรา',
        floor: 'stone',
        wall: 'stone',
        dark: 0.55,
        plan: [
          '###########',
          '#WWW.XX.WW#',
          '#.........#',
          '#W.*...*.X#',
          '#W.......X#',
          '#^..WW...G#',
          '###########',
        ],
      },
    ],
  },
  {
    id: 'yanyu-elder',
    // Its door opens south onto the elder's terrace (docs/archive/YANYU.md §5).
    facing: 's',
    name: 'บ้านผู้เฒ่าหลิว',
    zone: 'yanyu',
    template: 'house',
    seed: 23,
    floors: [
      {
        level: 0,
        name: 'บ้านผู้เฒ่าหลิว',
        floor: 'plank',
        wall: 'plaster',
        dark: 0.28,
        plan: [
          '##w#####w##',
          '#SS..A..SS#',
          '#.........#',
          '#.T.*..Z..#',
          '#.Tc...BB.#',
          '#..1...,,.#',
          '#K.....,,.#',
          '#X........#',
          '####D######',
        ],
        npcs: [
          {
            spot: '1',
            name: 'หลิวเหวิน',
            title: 'หลานชายผู้เฒ่าหลิว',
            sprite: 'scholar',
            lines: [
              'ท่านปู่ออกไปเฝ้าลานกลางหมู่บ้านทุกวัน ไม่เคยขาดสักวัน',
              'ตำราบนชั้นนั้นเป็นบันทึกของตระกูลหลิว ผู้เฝ้าศิลามาหลายชั่วคน',
              'ท่านปู่ไม่เคยเอ่ยชื่อศิษย์คนนั้นในบ้านนี้เลย',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'yanyu-store',
    // Its door opens north onto the market street.
    facing: 'n',
    name: 'ร้านค้าหยานหยู',
    zone: 'yanyu',
    template: 'shop',
    seed: 31,
    floors: [
      {
        level: 0,
        name: 'ร้านค้าหยานหยู',
        floor: 'tile',
        wall: 'plaster',
        dark: 0.2,
        plan: [
          '#w######w#',
          '#SSSSSSSS#',
          '#........#',
          '#CCCC1.*.#',
          '#........#',
          '#X.T..T.X#',
          '#X.c..c.W#',
          '####D#####',
        ],
        npcs: [
          {
            spot: '1',
            name: 'ป้าฟาง',
            title: 'คนดูแลร้าน',
            sprite: 'teaSeller',
            shop: true,
            lines: [
              'ของใช้ ยา เสบียง ของจากทั่วยุทธภพที่เถ้าแก่ฉางขนมา ร้านนี้มีครบ เลือกดูตามสบายนะ {name}',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'yanyu-shrine',
    facing: 'e',
    name: 'ศาลเจ้าชาวประมง',
    zone: 'yanyu',
    template: 'hall',
    seed: 41,
    floors: [
      {
        level: 0,
        name: 'ศาลเจ้าชาวประมง',
        floor: 'stone',
        wall: 'timber',
        dark: 0.4,
        plan: [
          '#####w#w#####',
          '#P...LAL...P#',
          '#.....h.....#',
          '#.,,,,,,,,,.#',
          '#P.,,,1,,,.P#',
          '#..,,,,,,,..#',
          '#*.........*#',
          '#P.X.....X.P#',
          '#...........#',
          '######D######',
        ],
        // The trapdoor before the altar stays shut until the first lesson of the waves is learned.
        sealed: { quest: 1, text: 'แผ่นไม้หน้าแท่นบูชาถูกตรึงไว้แน่น… ผู้เฝ้าศาลมองเจ้านิ่งๆ' },
        npcs: [
          {
            spot: '1',
            name: 'นักพรตเฝ้าศาล',
            title: 'ผู้ดูแลศาลเจ้า',
            sprite: 'taoist',
            lines: [
              'ชาวประมงจุดธูปที่นี่ก่อนออกเรือทุกเช้า ขอให้คลื่นพาพวกเขากลับฝั่ง',
              'คลื่นซัดเข้า คลื่นถอยกลับ คลื่นแตกฝั่ง… ผู้ที่ฟังทะเลเป็นย่อมเข้าใจ',
              'ใต้ศาลนี้มีห้องเก่าอยู่ ผู้ที่ยังไม่เข้าใจคลื่น อย่าเพิ่งลงไป',
            ],
          },
        ],
      },
      {
        level: -1,
        name: 'ห้องลับใต้ศาลเจ้า',
        floor: 'stone',
        wall: 'stone',
        dark: 0.62,
        plan: [
          '###########',
          '#S.L...L.S#',
          '#....A....#',
          '#.........#',
          '#u..*...*.#',
          '#.........#',
          '#X.......v#',
          '###########',
        ],
      },
      {
        level: -2,
        name: 'ถ้ำคลื่นใต้ศาลเจ้า',
        floor: 'earth',
        wall: 'rock',
        dark: 0.78,
        rules: DANGER_RULES,
        plan: [
          '  OOOOOOOOOO    ',
          ' OO...m....OOO  ',
          ' O..~~~.....mOO ',
          'OO.~~~~..O....O ',
          'O^..~~..OO..m.OO',
          'O.........*....O',
          'OO..m.OO.......O',
          ' O.....O..~~...O',
          ' OO.*......~~.OO',
          '  OO..m....OOO  ',
          '   OOOOOOOOOO   ',
        ],
        monsters: [
          { monster: 'viper', n: 3 },
          { monster: 'bandit', n: 2 },
        ],
      },
    ],
  },
  house('yanyu-hai', 'yanyu', 'บ้านลุงไห่', 51, { facing: 'e', residents: ['hai', 'yu'] }),
  house('yanyu-home-2', 'yanyu', 'บ้านชาวประมง', 52, { facing: 'w', mirror: true }),
  house('yanyu-home-3', 'yanyu', 'บ้านริมหาด', 54, { facing: 'e', swap: { N: 'X', B: 'G' } }),
  house('yanyu-zheng', 'yanyu', 'บ้านลุงเจิ้ง', 53, {
    facing: 'w',
    mirror: true,
    swap: { N: 'G' },
    residents: ['zheng'],
  }),
  house('yanyu-niu', 'yanyu', 'บ้านอาหนิว', 57, { facing: 'e', swap: { N: 'X' }, residents: ['niu'] }),
  house('yanyu-sun', 'yanyu', 'บ้านป้าซุน', 59, {
    facing: 'w',
    mirror: true,
    swap: { N: 'W' },
    residents: ['sun'],
  }),
  // TODO-polish (H10): plains placeholders on the yanyu-store shape; real plans and art later.
  {
    id: 'plains-warehouse',
    facing: 's',
    name: 'โรงคลัง',
    zone: 'plains',
    template: 'shop',
    seed: 61,
    floors: [
      {
        level: 0,
        name: 'โรงคลัง',
        floor: 'plank',
        wall: 'timber',
        dark: 0.3,
        plan: ['#w######w#', '#SSSSSSSS#', '#........#', '#CCCC1.*.#', '#........#', '#X.T..T.X#', '#X.c..c.W#', '####D#####'],
        npcs: [
          {
            spot: '1',
            name: 'ลุงเหลา',
            title: 'ผู้ดูแลโรงคลัง',
            sprite: 'merchant',
            lines: ['ของที่ฝากไว้ที่นี่ ไม่หายแน่ {name} มาเก็บมารับได้ทุกเมื่อ'],
          },
        ],
      },
    ],
  },
  {
    id: 'plains-trainhall',
    facing: 's',
    name: 'โรงฝึกยุทธ์',
    zone: 'plains',
    template: 'hall',
    seed: 62,
    floors: [
      {
        level: 0,
        name: 'โรงฝึกยุทธ์',
        floor: 'stone',
        wall: 'timber',
        dark: 0.3,
        plan: ['#w######w#', '#P......P#', '#........#', '#...1....#', '#........#', '#*......*#', '#P......P#', '####D#####'],
        npcs: [
          {
            spot: '1',
            name: 'ครูหลิน',
            title: 'ครูฝึกยุทธ์',
            sprite: 'scholar',
            lines: ['มาฝึกท่าพื้นฐานกันเถอะ {name} ฐานมั่นคง ก้าวต่อไปก็ไม่ล้ม'],
          },
        ],
      },
    ],
  },
  // ถ้ำคลื่นคำราม (data/dungeon.ts): the dungeon under the island shrine of the new Yanyu, run by systems/dungeon.ts.
  DUNGEON_INTERIOR,
  ...SECT_INTERIORS,
];

export const INTERIORS: Readonly<Record<string, InteriorDef>> = Object.fromEntries(
  list.map((d) => [d.id, d]),
);
export const INTERIOR_LIST: readonly InteriorDef[] = list;

/** Which interior each building's door opens onto. */
export const DOOR_LINKS: readonly DoorLink[] = [
  { zone: 'yanyu', building: 'yanyu-inn', interior: 'yanyu-inn' },
  { zone: 'yanyu', building: 'yanyu-elder-house', interior: 'yanyu-elder' },
  { zone: 'yanyu', building: 'yanyu-shop', interior: 'yanyu-store' },
  { zone: 'yanyu', building: 'yanyu-shrine', interior: 'yanyu-shrine' },
  { zone: 'yanyu', building: 'yanyu-home-hai', interior: 'yanyu-hai' },
  { zone: 'yanyu', building: 'yanyu-home-2', interior: 'yanyu-home-2' },
  { zone: 'yanyu', building: 'yanyu-home-3', interior: 'yanyu-home-3' },
  { zone: 'yanyu', building: 'yanyu-watch-house', interior: 'yanyu-zheng' },
  { zone: 'yanyu', building: 'yanyu-farm-niu', interior: 'yanyu-niu' },
  { zone: 'yanyu', building: 'yanyu-farm-sun', interior: 'yanyu-sun' },
  // The new Yanyu: the plaza's houses that have a room (data/layouts/yanyu2.ts PLAZA_IDS).
  { zone: 'yanyu2', building: 'yanyu-inn', interior: 'yanyu-inn' },
  { zone: 'yanyu2', building: 'yanyu-elder-house', interior: 'yanyu-elder' },
  { zone: 'yanyu2', building: 'yanyu-shop', interior: 'yanyu-store' },
  { zone: 'yanyu2', building: 'yanyu-shrine', interior: 'yanyu-shrine' },
  { zone: 'yanyu2', building: 'yanyu-home-hai', interior: 'yanyu-hai' },
  { zone: 'yanyu2', building: 'yanyu-home-2', interior: 'yanyu-home-2' },
  { zone: 'plains', building: 'plains-warehouse', interior: 'plains-warehouse' },
  { zone: 'plains', building: 'plains-trainhall', interior: 'plains-trainhall' },
  ...(['b', 'c', 'd'] as const).flatMap((t) => [
    { zone: `town-${t}` as const, building: `town-${t}-warehouse`, interior: 'plains-warehouse' },
    { zone: `town-${t}` as const, building: `town-${t}-trainhall`, interior: 'plains-trainhall' },
  ]), // H7 greybox: reuse the plains rooms (TODO-polish)
  DUNGEON_LINK,
  ...SECT_DOOR_LINKS,
  ...CITY_DOOR_LINKS,
];
